import { isImpersonationExchangeCode } from './config';

export const IMPERSONATION_MESSAGE = Object.freeze({
  READY: 'capitius:impersonation:ready',
  OFFER: 'capitius:impersonation:offer',
  CODE: 'capitius:impersonation:code',
});

export const IMPERSONATION_TRANSPORT_TIMEOUT_MS = 10_000;

const LAUNCH_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class ImpersonationTransportError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ImpersonationTransportError';
  }
}

export const isImpersonationLaunchId = (value) => (
  typeof value === 'string' && LAUNCH_ID_PATTERN.test(value)
);

export const createImpersonationLaunchId = (
  cryptoObject = typeof window !== 'undefined' ? window.crypto : undefined
) => {
  if (typeof cryptoObject?.randomUUID === 'function') {
    const value = cryptoObject.randomUUID();
    if (isImpersonationLaunchId(value)) return value;
  }

  if (typeof cryptoObject?.getRandomValues !== 'function') {
    throw new ImpersonationTransportError('Secure browser randomness is unavailable');
  }

  const bytes = cryptoObject.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const transportFailure = () => new ImpersonationTransportError(
  'Не удалось установить защищённое соединение со служебным окном.',
);

const isExpectedMessage = ({ event, source, origin, type, kind, launchId }) => (
  event?.source === source &&
  event?.origin === origin &&
  event?.data?.type === type &&
  event?.data?.kind === kind &&
  event?.data?.launchId === launchId
);

export const createImpersonationMessageSender = ({
  popup,
  kind,
  launchId,
  windowObject = window,
  MessageChannelImplementation = windowObject?.MessageChannel,
  timeoutMs = IMPERSONATION_TRANSPORT_TIMEOUT_MS,
} = {}) => {
  if (
    !popup ||
    !kind ||
    !isImpersonationLaunchId(launchId) ||
    typeof windowObject?.addEventListener !== 'function' ||
    typeof MessageChannelImplementation !== 'function'
  ) {
    throw transportFailure();
  }

  const expectedOrigin = windowObject.location.origin;
  let timer = null;
  let settled = false;
  let activePort = null;
  let rejectReady;
  let onMessage;

  const cleanup = () => {
    windowObject.removeEventListener('message', onMessage);
    if (timer !== null) windowObject.clearTimeout(timer);
    timer = null;
  };

  const ready = new Promise((resolve, reject) => {
    rejectReady = reject;

    function resolveWithPort(port) {
      if (settled) return;
      settled = true;
      activePort = port;
      cleanup();
      port.start?.();
      resolve(port);
    }

    // Function declaration is used so cleanup can safely reference it before
    // the first browser message is delivered.
    onMessage = (event) => {
      if (!isExpectedMessage({
        event,
        source: popup,
        origin: expectedOrigin,
        type: IMPERSONATION_MESSAGE.READY,
        kind,
        launchId,
      })) return;

      try {
        const channel = new MessageChannelImplementation();
        popup.postMessage(
          { type: IMPERSONATION_MESSAGE.OFFER, kind, launchId },
          expectedOrigin,
          [channel.port2],
        );
        resolveWithPort(channel.port1);
      } catch {
        if (settled) return;
        settled = true;
        cleanup();
        reject(transportFailure());
      }
    };

    windowObject.addEventListener('message', onMessage);
    timer = windowObject.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(transportFailure());
    }, timeoutMs);
  });

  const cancel = () => {
    if (settled) {
      activePort?.close?.();
      return;
    }

    settled = true;
    cleanup();
    rejectReady?.(transportFailure());
  };

  return { cancel, ready };
};

export const createImpersonationMessageReceiver = ({
  kind,
  launchId,
  windowObject = window,
  timeoutMs = IMPERSONATION_TRANSPORT_TIMEOUT_MS,
} = {}) => {
  const expectedOrigin = windowObject?.location?.origin;
  const expectedOpener = windowObject?.opener;
  if (
    !kind ||
    !isImpersonationLaunchId(launchId) ||
    !expectedOrigin ||
    !expectedOpener ||
    typeof windowObject?.addEventListener !== 'function'
  ) {
    throw transportFailure();
  }

  let timer = null;
  let settled = false;
  let activePort = null;
  let rejectCode;
  let onOffer;

  const cleanupWindowListener = () => {
    windowObject.removeEventListener('message', onOffer);
  };

  const cleanup = () => {
    cleanupWindowListener();
    if (timer !== null) windowObject.clearTimeout(timer);
    timer = null;
    if (activePort) {
      activePort.onmessage = null;
      activePort.onmessageerror = null;
      activePort.close?.();
      activePort = null;
    }
  };

  const code = new Promise((resolve, reject) => {
    rejectCode = reject;

    const fail = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(transportFailure());
    };

    onOffer = (event) => {
      if (!isExpectedMessage({
        event,
        source: expectedOpener,
        origin: expectedOrigin,
        type: IMPERSONATION_MESSAGE.OFFER,
        kind,
        launchId,
      })) return;

      const [port] = event.ports || [];
      if (!port) {
        fail();
        return;
      }

      cleanupWindowListener();
      activePort = port;

      try {
        windowObject.opener = null;
      } catch {
        fail();
        return;
      }
      if (windowObject.opener !== null) {
        fail();
        return;
      }

      port.onmessage = (portEvent) => {
        if (
          portEvent?.data?.type !== IMPERSONATION_MESSAGE.CODE ||
          portEvent?.data?.kind !== kind ||
          portEvent?.data?.launchId !== launchId ||
          !isImpersonationExchangeCode(portEvent?.data?.code)
        ) {
          fail();
          return;
        }

        if (settled) return;
        const exchangeCode = portEvent.data.code;
        settled = true;
        cleanup();
        resolve(exchangeCode);
      };
      port.onmessageerror = fail;
      port.start?.();
    };

    windowObject.addEventListener('message', onOffer);
    timer = windowObject.setTimeout(fail, timeoutMs);

    try {
      expectedOpener.postMessage(
        { type: IMPERSONATION_MESSAGE.READY, kind, launchId },
        expectedOrigin,
      );
    } catch {
      fail();
    }
  });

  const cancel = () => {
    if (settled) return;
    settled = true;
    cleanup();
    rejectCode?.(transportFailure());
  };

  return { cancel, code };
};
